
import { Language } from '@/context/languages/languageData';

// Mock translations para demostración - en implementación real vendría de archivos de traducción
const mockTranslations: Record<string, Partial<Record<Language, string>>> = {
  'heroTitle': {
    en: 'Find the Best Therapists Worldwide',
    es: 'Encuentra los Mejores Terapeutas del Mundo',
    fr: 'Trouvez les Meilleurs Thérapeutes du Monde',
    de: 'Finden Sie die Besten Therapeuten Weltweit'
  },
  'firstName': {
    en: 'First Name',
    es: 'Nombre',
    fr: 'Prénom',
    de: 'Vorname'
  },
  'send': {
    en: 'Send',
    es: 'Enviar',
    fr: 'Envoyer',
    de: 'Senden'
  }
};

export interface TranslationIssue {
  key: string;
  component: string;
  missingLanguages: Language[];
  hasDefaultText: boolean;
  defaultText?: string;
  currentTranslations: Partial<Record<Language, string>>;
  severity: 'high' | 'medium' | 'low';
}

export interface ComponentAnalysis {
  componentName: string;
  filePath: string;
  translationKeys: string[];
  hardcodedText: string[];
  issues: TranslationIssue[];
}

export interface TranslationAnalysisReport {
  totalComponents: number;
  totalTranslationKeys: number;
  totalIssues: number;
  componentAnalysis: ComponentAnalysis[];
  missingTranslationsByLanguage: Partial<Record<Language, number>>;
  hardcodedTextInstances: number;
}

const supportedLanguages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ko', 'ja', 'zh', 'ar', 'hi', 'ru', 'tr', 'th', 'vi', 'id', 'nl', 'ur', 'bn', 'fa', 'ms'];

// Función para extraer claves de traducción de un texto
export const extractTranslationKeys = (text: string): string[] => {
  const tKeyRegex = /t\(['"`]([^'"`]+)['"`]\)/g;
  const keys: string[] = [];
  let match;
  
  while ((match = tKeyRegex.exec(text)) !== null) {
    keys.push(match[1]);
  }
  
  return keys;
};

// Función para extraer texto hardcodeado (no traducido)
export const extractHardcodedText = (text: string): string[] => {
  const hardcodedText: string[] = [];
  
  // Buscar strings en JSX que no sean claves de traducción
  const jsxTextRegex = />([^<>{]+)</g;
  const stringLiteralRegex = /['"`]([^'"`]{10,})['"`]/g;
  
  let match;
  
  // Texto en JSX
  while ((match = jsxTextRegex.exec(text)) !== null) {
    const content = match[1].trim();
    if (content && 
        !content.includes('{') && 
        !content.startsWith('t(') &&
        content.length > 3 &&
        /[a-zA-Z]/.test(content)) {
      hardcodedText.push(content);
    }
  }
  
  // String literals largos
  while ((match = stringLiteralRegex.exec(text)) !== null) {
    const content = match[1].trim();
    if (content && 
        !content.includes('http') && 
        !content.includes('className') &&
        /[a-zA-Z]/.test(content)) {
      hardcodedText.push(content);
    }
  }
  
  return hardcodedText;
};

// Función para analizar las traducciones existentes
export const analyzeTranslationKey = (key: string): TranslationIssue | null => {
  const translation = mockTranslations[key];
  
  if (!translation) {
    return {
      key,
      component: 'unknown',
      missingLanguages: supportedLanguages,
      hasDefaultText: false,
      currentTranslations: {},
      severity: 'high'
    };
  }
  
  const missingLanguages: Language[] = [];
  const currentTranslations: Partial<Record<Language, string>> = {};
  
  supportedLanguages.forEach(lang => {
    if (translation[lang]) {
      currentTranslations[lang] = translation[lang];
    } else {
      missingLanguages.push(lang);
    }
  });
  
  if (missingLanguages.length === 0) {
    return null; // No hay problemas
  }
  
  return {
    key,
    component: 'unknown',
    missingLanguages,
    hasDefaultText: !!translation.en,
    defaultText: translation.en,
    currentTranslations,
    severity: missingLanguages.length > 3 ? 'high' : missingLanguages.length > 1 ? 'medium' : 'low'
  };
};

// Función para analizar un componente específico
export const analyzeComponent = (componentName: string, filePath: string, content: string): ComponentAnalysis => {
  const translationKeys = extractTranslationKeys(content);
  const hardcodedText = extractHardcodedText(content);
  const issues: TranslationIssue[] = [];
  
  translationKeys.forEach(key => {
    const issue = analyzeTranslationKey(key);
    if (issue) {
      issue.component = componentName;
      issues.push(issue);
    }
  });
  
  return {
    componentName,
    filePath,
    translationKeys,
    hardcodedText,
    issues
  };
};

// Función principal para generar el reporte completo
export const generateTranslationReport = (componentsData: { name: string; path: string; content: string }[]): TranslationAnalysisReport => {
  const componentAnalysis: ComponentAnalysis[] = [];
  let totalIssues = 0;
  let totalTranslationKeys = 0;
  let hardcodedTextInstances = 0;
  const missingTranslationsByLanguage: Partial<Record<Language, number>> = {
    en: 0, es: 0, fr: 0, de: 0, it: 0, pt: 0, ko: 0, ja: 0, zh: 0, ar: 0, hi: 0, ru: 0, tr: 0, th: 0, vi: 0, id: 0, nl: 0, ur: 0, bn: 0, fa: 0, ms: 0
  };
  
  componentsData.forEach(({ name, path, content }) => {
    const analysis = analyzeComponent(name, path, content);
    componentAnalysis.push(analysis);
    
    totalTranslationKeys += analysis.translationKeys.length;
    hardcodedTextInstances += analysis.hardcodedText.length;
    totalIssues += analysis.issues.length;
    
    analysis.issues.forEach(issue => {
      issue.missingLanguages.forEach(lang => {
        missingTranslationsByLanguage[lang]++;
      });
    });
  });
  
  return {
    totalComponents: componentsData.length,
    totalTranslationKeys,
    totalIssues,
    componentAnalysis,
    missingTranslationsByLanguage,
    hardcodedTextInstances
  };
};

// Función para sugerir claves de traducción para texto hardcodeado
export const suggestTranslationKey = (text: string, component: string): string => {
  // Convertir a camelCase y limpiar
  const cleanText = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
  
  const componentPrefix = component.toLowerCase().replace(/component|section|page/gi, '');
  
  return `${componentPrefix}${cleanText.charAt(0).toUpperCase() + cleanText.slice(1)}`;
};
